import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { useEffect, useState } from "react";

import { type WorksheetGeneral } from "./useSheetReader";

const EMAIL_REGEX = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const STRING = "string";

const Message = {
  EMAIL_REQUIRED: "Email is required",
  EMAIL_TYPE: "Email must be a string",
  FIRST_REQUIRED: "First name is required",
  FIRST_TYPE: "First name must be a string",
  LAST_REQUIRED: "Last name is required",
  LAST_TYPE: "Last name must be a string",
  ROLE_REQUIRED: "Role is required",
  ROLE_TYPE: "Role must be a string",
  SCHOOL_REQUIRED: 'School is required when Role is "educator"',
  SCHOOL_TYPE: "School must be a string",
  SHEET_INVALID_ORGANIZATION: 'Sheet must contain "First Name", "Last Name", "Email", and "Role" columns in that order',
  SHEET_INVALID_SCHOOL: 'Sheet must contain "First", "Last", "Email", "School", and "Role" columns in that order',
};

/** @deprecated We are phasing out `cc-ui` */
export interface GeneralError {
  count: number;
  messages: string[];
}

/** @deprecated We are phasing out `cc-ui` */
export interface Error {
  invalid: GeneralError;
  required: GeneralError;
  type: GeneralError;
}

/** @deprecated We are phasing out `cc-ui` */
export interface WorksheetErrors {
  email: Error;
  first: Error;
  last: Error;
  role: Error;
  school: Error;
  sheet: Error;
}

/** @deprecated We are phasing out `cc-ui` */
export type BulkUploadSheetValidationVariant = "school" | "organization";

export interface UseBulkUploadSheetValidationOptions {
  /** Role strings from `useBubbleGetRoles` (same source as Add User). When empty, role allowlist validation is skipped (e.g. while roles are loading). */
  allowedRoles?: string[];
  schools?: string[];
  worksheet?: WorksheetGeneral;
  /** @default "school" */
  variant?: BulkUploadSheetValidationVariant;
}

/** @deprecated We are phasing out `cc-ui` */
export interface UseBulkUploadSheetValidationReturn {
  errorsRaw: WorksheetErrors;
  errors: string[];
}

/** @deprecated We are phasing out `cc-ui` */
const ERROR_TRACKER_INITIAL: WorksheetErrors = {
  email: {
    invalid: {
      count: 0,
      messages: [],
    },
    required: {
      count: 0,
      messages: [Message.EMAIL_REQUIRED],
    },
    type: {
      count: 0,
      messages: [Message.EMAIL_TYPE],
    },
  },
  first: {
    invalid: {
      count: 0,
      messages: [],
    },
    required: {
      count: 0,
      messages: [Message.FIRST_REQUIRED],
    },
    type: {
      count: 0,
      messages: [Message.FIRST_TYPE],
    },
  },
  last: {
    invalid: {
      count: 0,
      messages: [],
    },
    required: {
      count: 0,
      messages: [Message.LAST_REQUIRED],
    },
    type: {
      count: 0,
      messages: [Message.LAST_TYPE],
    },
  },
  role: {
    invalid: {
      count: 0,
      messages: [],
    },
    required: {
      count: 0,
      messages: [Message.ROLE_REQUIRED],
    },
    type: {
      count: 0,
      messages: [Message.ROLE_TYPE],
    },
  },
  school: {
    invalid: {
      count: 0,
      messages: [],
    },
    required: {
      count: 0,
      messages: [Message.SCHOOL_REQUIRED],
    },
    type: {
      count: 0,
      messages: [Message.SCHOOL_TYPE],
    },
  },
  sheet: {
    invalid: {
      count: 0,
      messages: [Message.SHEET_INVALID_SCHOOL],
    },
    required: {
      count: 0,
      messages: [],
    },
    type: {
      count: 0,
      messages: [],
    },
  },
};

/** @deprecated We are phasing out `cc-ui` */
export const useBulkUploadSheetValidation: (
  options: UseBulkUploadSheetValidationOptions,
) => UseBulkUploadSheetValidationReturn = ({
  allowedRoles = [],
  schools: knownSchools,
  worksheet,
  variant = "school",
}) => {
  const [errorsRaw, setErrorsRaw] = useState(cloneDeep(ERROR_TRACKER_INITIAL));
  const [errors, setErrors] = useState<string[]>([]);
  const isOrganizationVariant = variant === "organization";
  const requiredColumnCount = isOrganizationVariant ? 4 : 5;

  useEffect(() => {
    if (!worksheet) {
      setErrorsRaw(ERROR_TRACKER_INITIAL);
      setErrors([]);
    }
  }, [worksheet]);

  useEffect(() => {
    if (!worksheet) {
      return;
    }

    setErrorsRaw((prev) => {
      const updatedErrorsRaw = cloneDeep(ERROR_TRACKER_INITIAL);
      if (isOrganizationVariant) {
        updatedErrorsRaw.sheet.invalid.messages = [Message.SHEET_INVALID_ORGANIZATION];
      }

      if (worksheet.headers && worksheet.headers[0].length < requiredColumnCount) {
        updatedErrorsRaw.sheet.invalid.count += 1;
        return isEqual(prev, updatedErrorsRaw) ? prev : updatedErrorsRaw;
      }

      const allowedRoleLower = new Set(allowedRoles.map((r) => r.toLowerCase()));
      const validRolesLabel = [...allowedRoles].sort().join(", ");

      for (const row of worksheet.rows) {
        const first = row[0];
        const last = row[1];
        const email = row[2];
        const school = isOrganizationVariant ? undefined : row[3];
        const role = isOrganizationVariant ? row[3] : row[4];
        const roleLower = typeof role === "string" ? role.toLowerCase() : "";

        // required
        if (!first) {
          updatedErrorsRaw.first.required.count += 1;
        }
        if (!last) {
          updatedErrorsRaw.last.required.count += 1;
        }
        if (!email) {
          updatedErrorsRaw.email.required.count += 1;
        }
        if (!isOrganizationVariant && !school && roleLower === "educator") {
          updatedErrorsRaw.school.required.count += 1;
        }
        if (!isOrganizationVariant && !school && roleLower === "admin") {
          updatedErrorsRaw.school.required.count += 1;
        }
        if (!role) {
          updatedErrorsRaw.role.required.count += 1;
        }

        // type
        if (first && typeof first !== STRING) {
          updatedErrorsRaw.first.type.count += 1;
        }
        if (last && typeof last !== STRING) {
          updatedErrorsRaw.last.type.count += 1;
        }
        if (email && typeof email !== STRING) {
          updatedErrorsRaw.email.type.count += 1;
        }
        if (!isOrganizationVariant && school && typeof school !== STRING) {
          updatedErrorsRaw.school.type.count += 1;
        }
        if (role && typeof role !== STRING) {
          updatedErrorsRaw.role.type.count += 1;
        }

        // values
        if (role && allowedRoleLower.size > 0 && !allowedRoleLower.has(roleLower)) {
          updatedErrorsRaw.role.invalid.count += 1;
          const message = `Role '${role}' invalid. Valid roles include: ${validRolesLabel}`;
          if (!updatedErrorsRaw.role.invalid.messages.includes(message)) {
            updatedErrorsRaw.role.invalid.messages.push(message);
          }
        }
        if (email && !EMAIL_REGEX.test(email)) {
          updatedErrorsRaw.email.invalid.count += 1;
          const message = `Email '${email}' invalid. Valid emails look like 'mycoolemail@someprovider.com`;
          if (!updatedErrorsRaw.email.invalid.messages.includes(message)) {
            updatedErrorsRaw.email.invalid.messages.push(message);
          }
        }
        if (!isOrganizationVariant && school && !knownSchools?.includes(school)) {
          updatedErrorsRaw.school.invalid.count += 1;
          const message = `School '${school}' invalid. School names are case sensitive and must match the list of known schools`;
          if (!updatedErrorsRaw.school.invalid.messages.includes(message)) {
            updatedErrorsRaw.school.invalid.messages.push(message);
          }
        }
      }

      return isEqual(prev, updatedErrorsRaw) ? prev : updatedErrorsRaw;
    });
  }, [allowedRoles, isOrganizationVariant, knownSchools, requiredColumnCount, worksheet]);

  useEffect(() => {
    const updatedErrors = Object.keys(errorsRaw)
      .map((k) => {
        const typedK = k as keyof WorksheetErrors;
        const worksheetErrorField = errorsRaw[typedK];

        return Object.keys(worksheetErrorField)
          .map((kk) => {
            const typedKk = kk as keyof Error;
            const errorField = worksheetErrorField[typedKk];

            if (errorField.count) {
              return typedKk === "invalid" && ["role", "school", "sheet"].includes(typedK)
                ? errorField.messages
                : errorField.messages.map((msg) => `${errorField.count} error(s): ${msg}`);
            }
          })
          .filter((value) => (Array.isArray(value) ? !!value.length : !!value))
          .reduce<string[]>((a, b) => (a && b ? [...a, ...b] : [...a]), []);
      })
      .filter((value) => (Array.isArray(value) ? !!value.length : !!value))
      .reduce<string[]>((a, b) => (a && b ? [...a, ...b] : [...a]), [])
      .sort();
    setErrors(updatedErrors);
  }, [errorsRaw]);

  return { errors, errorsRaw };
};
